import {
  Box,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { CloseIcon, LowVisionIcon } from 'components/icons';
import { OverlayProps } from 'components/TaskOverlay';
import React, { FocusEventHandler, useEffect, useState } from 'react';
import {
  FieldName,
  FieldPathValue,
  FieldValues,
  useForm,
  Validate,
} from 'react-hook-form';
import { AlertDialog } from './AlertDialog';

interface InputDialogProps extends OverlayProps {
  title: string;
  submitButtonLabel: string;
  placeholder?: string;
  type?: string;
  defaultValue?: string;

  onSubmitInput(input: string): void;

  isRequired?: boolean;
  isLoading?: boolean;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  'data-qa'?: string;
  validation?:
    | Validate<FieldPathValue<FieldValues, FieldName<FieldValues>>>
    | Record<
        string,
        Validate<FieldPathValue<FieldValues, FieldName<FieldValues>>>
      >;
}

export const InputDialog = ({
  title,
  submitButtonLabel,
  placeholder,
  type = 'text',
  defaultValue = '',
  onSubmitInput,
  isOpen,
  onClose,
  isRequired = true,
  isLoading,
  onFocus,
  children,
  validation = {},
  ...props
}: InputDialogProps) => {
  const focusRef = React.useRef(null);
  const [wasOpen, setWasOpen] = useState(false);
  const [inputType, setInputType] = useState(type);

  const { setFocus, register, handleSubmit, setValue, formState } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      input: defaultValue,
    },
  });

  const clearInput = () => {
    setValue('input', defaultValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setFocus('input');
  };

  const errorMessage = formState.errors['input']?.message;

  const togglePassword = () => {
    setInputType(inputType === 'text' ? 'password' : 'text');
  };

  const onSubmit = ({ input }: { input: string }) => {
    if (!isRequired || input !== defaultValue) {
      onSubmitInput(input);
    } else {
      onClose();
    }
  };

  useEffect(() => {
    if (wasOpen !== isOpen) {
      if (isOpen === false) {
        setValue('input', `${defaultValue}`, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      setWasOpen(isOpen);
    }
  }, [wasOpen, isOpen]);

  return (
    <AlertDialog
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      actionButton={{
        onClick: handleSubmit(onSubmit),
        title: submitButtonLabel,
        isDisabled: !formState.isValid,
        isLoading,
      }}
      cancelButton={{ title: t`Cancel`, onClick: onClose }}
      leastDestructiveRef={focusRef}
      data-qa={props['data-qa']}
    >
      <Stack>
        {children}
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputGroup>
            <Input
              {...register('input', {
                required: isRequired,
                ...(validation ? { validate: validation } : {}),
              })}
              type={inputType}
              placeholder={placeholder}
              onFocus={onFocus}
            />
            <InputRightElement>
              {type === 'text' ? (
                <IconButton
                  borderRadius="full"
                  size="xs"
                  onClick={clearInput}
                  aria-label={t`Clear input`}
                >
                  <CloseIcon />
                </IconButton>
              ) : (
                <IconButton
                  borderRadius="full"
                  size="xs"
                  onClick={togglePassword}
                  aria-label={t`Toggle password`}
                >
                  <LowVisionIcon />
                </IconButton>
              )}
            </InputRightElement>
          </InputGroup>
          {errorMessage ? (
            <Box mt={2} color="red.500">
              {errorMessage}
            </Box>
          ) : null}
        </form>
      </Stack>
    </AlertDialog>
  );
};
